import type { SortKey, ViewMode } from '@mona-roadmap/domain';

/**
 * `tree` is the Product Goal / Epic tree column. `name` is the Deliverable column: its key stays `name` because the
 * name sort, the name text filter and the "Name" filter chip all mean the deliverable's name.
 */
export type ColumnKey = 'tree' | 'name' | 'itemId' | 'goal' | 'epic' | 'productArea' | 'type' | 'pmOwner' | 'assignee' | 'sprint' | 'scopingStatus' | 'scopingDue' | 'status' | 'progress';

export type MultiFilterKey = 'goals' | 'epics' | 'productAreas' | 'types' | 'pmOwners' | 'assignees' | 'sprints' | 'scopingStatuses' | 'statuses';

export type ColumnFilter = { kind: 'text' } | { kind: 'multi'; key: MultiFilterKey } | { kind: 'date' };

export interface ColumnDef {
  key: ColumnKey;
  label: string;
  width: string;
  filter?: ColumnFilter;
  hint?: string;
}

const TREE_GOAL: ColumnDef = { key: 'tree', label: 'Goal / Epic', width: 'minmax(300px, 1fr)', hint: 'Product Goals and Epics. Each shows once, with its first deliverable on the same row' };
const TREE_EPIC: ColumnDef = { key: 'tree', label: 'Epic', width: 'minmax(300px, 1fr)', hint: 'Epics. Each shows once, with its first deliverable on the same row' };
const NAME: ColumnDef = { key: 'name', label: 'Deliverable', width: 'minmax(300px, 1fr)', filter: { kind: 'text' } };
const ITEM_ID: ColumnDef = { key: 'itemId', label: 'ID', width: '112px', hint: "monday's item ID for deliverables. Click a value to copy it" };
const GOAL: ColumnDef = { key: 'goal', label: 'Product Goal', width: '210px', filter: { kind: 'multi', key: 'goals' }, hint: 'Product Goals this deliverable belongs to' };
/** Not a grid column (epics are tree rows); only exists as a filter. */
const EPIC: ColumnDef = { key: 'epic', label: 'Epic', width: '0px', filter: { kind: 'multi', key: 'epics' }, hint: 'Epic this deliverable belongs to' };

const STATUS: ColumnDef = { key: 'status', label: 'Status', width: '116px', filter: { kind: 'multi', key: 'statuses' }, hint: 'Grey = not started, blue = in progress, green = done' };

const REST: ColumnDef[] = [
  { key: 'productArea', label: 'Product Area', width: '176px', filter: { kind: 'multi', key: 'productAreas' }, hint: "The Epic's. Shown once, on the Epic's row" },
  { key: 'type', label: 'Type', width: '112px', filter: { kind: 'multi', key: 'types' }, hint: "External or internal. The Epic's, shown once on the Epic's row; its deliverables inherit it" },
  { key: 'assignee', label: 'Assignee', width: '140px', filter: { kind: 'multi', key: 'assignees' } },
  { key: 'pmOwner', label: 'PM Owner', width: '146px', filter: { kind: 'multi', key: 'pmOwners' } },
  { key: 'sprint', label: 'Sprint', width: '200px', filter: { kind: 'multi', key: 'sprints' } },
  { key: 'scopingStatus', label: 'Scoping', width: '136px', filter: { kind: 'multi', key: 'scopingStatuses' }, hint: 'Scoping Status' },
  { key: 'scopingDue', label: 'Scoping Due', width: '168px', filter: { kind: 'date' }, hint: 'Scoping Due Date' },
  { key: 'progress', label: 'Done/Total', width: '184px', hint: "Done / Total deliverables. Belongs to the Goal / Epic, shown once on its row" },
];

export function columnsFor(view: ViewMode): ColumnDef[] {
  return view === 'by-epic' ? [TREE_EPIC, NAME, STATUS, ITEM_ID, GOAL, ...REST] : [TREE_GOAL, NAME, STATUS, ITEM_ID, ...REST];
}

/** Everything the filter editor can filter on: the grid columns plus Product Goal and Epic in every view. */
export function filterColumnsFor(view: ViewMode): ColumnDef[] {
  const grid = columnsFor(view);
  const name = grid.find((c) => c.key === 'name')!;
  const rest = grid.filter((c) => c.key !== 'tree' && c.key !== 'name' && c.key !== 'goal' && c.key !== 'itemId');
  return [name, GOAL, EPIC, ...rest]; // Name, Product Goal, Epic, Status, Product Area, ...
}

/** The sort key a column header click selects, if the column can be sorted. */
export function sortKeyOf(c: ColumnDef): SortKey | null {
  return c.key === 'progress' || c.key === 'epic' || c.key === 'tree' ? null : (c.key as SortKey);
}

export const gridTemplate = (cols: ColumnDef[]): string => cols.map((c) => c.width).join(' ');
